import { MapControls as ThreeMapControls } from 'three/addons/controls/MapControls.js'
import { CONTROL_DAMPING_FACTOR } from '@/shared/constants'
import {
  type NativeControlServices,
  syncRigFromCamera,
  syncViewportCameraFromRig,
} from './NativeControlUtils'

/** MapController 构造参数 */
export interface MapControllerOptions {
  /** 控制器共享服务集合 */
  services: NativeControlServices
}

/** Map mode powered by Three's official MapControls. */
export class MapController extends ThreeMapControls {
  private services: NativeControlServices | null = null

  constructor(options: MapControllerOptions) {
    const camera = syncViewportCameraFromRig(options.services)
    super(camera)

    this.services = options.services
    this.enabled = false
    this.enableDamping = true
    this.dampingFactor = CONTROL_DAMPING_FACTOR
    this.screenSpacePanning = false
    this.target.copy(options.services.rig.target)
  }

  /**
   * 连接 DOM 元素并同步相机状态。
   *
   * @param element - 承载交互事件的 HTML 元素
   */
  override connect(element: HTMLElement): void {
    if (!this.services) return
    const camera = this.syncFromRig()
    this.target.copy(this.services.rig.target)
    camera.updateMatrixWorld(true)
    super.connect(element)
    super.update()
  }

  /**
   * 每帧更新控制器状态。
   *
   * @param deltaTime - 自上一帧的时间间隔，单位秒
   * @returns true 表示发生了可见的相机变更；false 表示无变更
   */
  override update(deltaTime?: number | null): boolean {
    if (!this.enabled || !this.services) return false

    const camera = this.syncFromRig()
    this.target.copy(this.services.rig.target)
    const changed = super.update(deltaTime)
    camera.updateMatrixWorld(true)
    syncRigFromCamera(this.services, camera, this.target)
    return changed
  }

  /** 释放控制器资源 */
  override dispose(): void {
    if (this.domElement) {
      super.dispose()
    }
  }

  private syncFromRig(): ReturnType<typeof syncViewportCameraFromRig> {
    if (!this.services) {
      return this.object as ReturnType<typeof syncViewportCameraFromRig>
    }

    const camera = syncViewportCameraFromRig(this.services)
    if (this.object !== camera) {
      this.object = camera
    }
    return camera
  }
}
